import { useStoreApi } from "@store/store";

/**
 * Will set isLocked on store.data.isLocked and run a wrapped function.
 *
 * This can be used for disabling buttons and whatever else will manipulate the current cart.
 * We do this to avoid cart version mismatch, which will happen if multiple operations
 * are executed on the same cart version.
 *
 * Subscribers can listen to store.data.isLocked
 */
export const useLock = () => {
    const api = useStoreApi();

    const unlock = () => {
        api.setState((state) => {
            return {
                ...state,
                data: {
                    ...state.data,
                    isLocked: false,
                },
            };
        });
    };

    const lock = () => {
        api.setState((state) => {
            return {
                ...state,
                data: {
                    ...state.data,
                    isLocked: true,
                },
            };
        });
    };

    return function <TCallback extends (...args: unknown[]) => unknown>(
        fn: TCallback
    ): (...args: Parameters<TCallback>) => Promise<Awaited<ReturnType<TCallback>>> {
        return async (...args: Parameters<TCallback>): Promise<Awaited<ReturnType<TCallback>>> => {
            // We set the isLocked state outside the React render cycle,
            // so we don't use setLocked.
            lock();

            try {
                const result = (await fn(...args)) as Awaited<ReturnType<TCallback>>;
                unlock();
                return Promise.resolve(result);
            } catch (error) {
                unlock();
                throw error;
            }
        };
    };
};
